import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import dayjs from 'dayjs'
import Tooltip from '../../../atoms/Tooltip'
import { DATE_FORMAT } from '../../../../constants'
import { useHideIfNoDataWhen } from '../../HideIfNoData'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import OpalPresentationTable, { usePresentationTableExpandState } from '../../DenaliAnalysis/tables/presentation'
import { usePipelineDataContextWithVariables } from '../provider/DDPDataProvider'
import { getInitialExpandedState } from '../../DenaliAnalysis/tables/presentation/utils'
import { localStorageHelper } from '../../../../utils/localStorageHelper'
import SydButton from '../../../commonDesign/Button'
import Icon from '../../../atoms/Icon'
import { useVariables } from '../../../../providers/VariableProvider'
import { ExportTableToggle } from './ExportTableToggle'
import { TableConfigModal } from './TableConfigModal'
import { CustomTooltip } from './AdditionalDateConfigContent'
const mapAccessorKey = column => column.accessorKey ? column.accessorKey : column.accessor
const mapSize = column => column.size ? column.size : column.width
const mapMaxSize = column => column.maxSize ? column.maxSize : column.maxWidth
const mapMinSize = column => column.minSize ? column.minSize : column.minWidth
const mapHeader = column => column.header ? column.header : column.Header

export const useColumns = (columns, totals) => {
  const { formatter } = useFormattingContext()

  return useMemo(() => {
    const defaultSort = []
    const includeFooter = totals?.length > 0
    const mapColumnProperties = (column) => {
      const accessorKey = mapAccessorKey(column)
      return {
        ...column,
        header: mapHeader(column),
        accessorKey: accessorKey,
        size: mapSize(column),
        maxSize: mapMaxSize(column),
        minSize: mapMinSize(column),
        meta: {
          ...column.meta,
          highlight: (row) => {
            return row?.original?.comparison?.includes(accessorKey) ?? false
          }
        }
      }
    }
    const addFormatting = (column, mappedColumn, accessorKey) => {
      if (!mappedColumn?.cell) {
        mappedColumn.cell = (props) =>
          formatter(props.getValue(), column.format)
      }
      if (includeFooter && !mappedColumn?.footer) {
        const total = totals.at(0)
        mappedColumn.footer = () => formatter(total[accessorKey], column.format)
      }
      return mappedColumn
    }

    const templatedColumns = columns?.map((column) => {
      const result = mapColumnProperties(column)

      if (result.columns) {
        result.columns = column.columns.map((subColumn) => {
          const subColResult = mapColumnProperties(subColumn)
          return addFormatting(subColumn, subColResult, mapAccessorKey(subColumn))
        })
      }

      return addFormatting(column, result, mapAccessorKey(column))
    })

    return {
      columns: templatedColumns,
      defaultSort,
      includeFooter
    }
  }, [formatter, columns, totals])
}
function PipelinePresentationTable ({
  pipeline,
  columnConfig,
  headerMode,
  className,
  style,
  componentClasses,
  componentStyles,
  dataAccessor,
  variables,
  emptyStateText,
  initialExpandDepth,
  allowExport,
  configurationOptions,
  ...tableProps
}) {
  const {
    localStorageKey,
    showTableConfiguration = ['configureColumns', 'configureGroupings', 'configureDates'],
    availableGroupings,
    availableDates,
    additionalDateTooltipStyle,
    additionalDateTooltipText = 'This Column is using an alternate date range',
    editableColumns = [],
    availableDateRanges = []
  } = configurationOptions || {}
  const { tryLoad, deHashObject, hashObjects, store } = localStorageHelper
  const [tableGroups, setTableGroups] = useState([])
  const [tableDates, setTableDates] = useState({
    startDate: null,
    endDate: null
  })
  const { getVariable } = useVariables()
  const quickDate = availableDateRanges.length > 0 ? availableDateRanges[0] : getVariable('quickDate') || 'day'
  const [tableAdditionalDates, setTableAdditionalDates] = useState({ editableColumns: editableColumns.map((column) => ({ dateRange: quickDate, column: column })), dateRange1: quickDate, dateRange2: quickDate })
  const [columnConfigOpen, setColumnConfigOpen] = useState(false)
  const [tableConfig, setTableConfig] = useState(columnConfig ?? [])
  const tableVariables = useMemo(() => {
    const groups = tableGroups?.length > 0 ? tableGroups.map((group) => group) : []
    const { startDate, endDate } = tableDates.startDate && tableDates.endDate ? {
      startDate: tableDates.startDate,
      endDate: tableDates.endDate
    } : { startDate: null, endDate: null }
    return {
      filters: {
        ...variables,
        ...tableAdditionalDates,
        ...(groups.length > 0 ? { grouping: groups } : {}),
        ...(startDate ? { startDate: startDate } : {}),
        ...(endDate ? { endDate: endDate } : {})
      }
    }
  }, [variables, tableGroups, tableDates, tableAdditionalDates])
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, tableVariables)

  const tableData = useMemo(() => get(data, dataAccessor), [data, dataAccessor])

  // Local Storage UseEffects
  /** Load the column configuration from localStorage */
  useEffect(() => {
    if (!localStorageKey) return
    const currentHash = hashObjects(columnConfig)
    const storedRecord = tryLoad(`${localStorageKey}.${currentHash}`)
    if (storedRecord) {
      const savedConfig = storedRecord ? deHashObject(storedRecord) : null
      setTableConfig(savedConfig ?? columnConfig)
    } else {
      store(`${localStorageKey}.${currentHash}`, currentHash)
      setTableConfig(columnConfig)
    }
  }, [localStorageKey, columnConfig, tryLoad, deHashObject, hashObjects, store])
  /** Load the available groupings from localStorage */
  useEffect(() => {
    if (!localStorageKey) return
    const currentHash = hashObjects(availableGroupings)
    const storedRecord = tryLoad(`${localStorageKey}.${currentHash}`)
    if (storedRecord) {
      const savedConfig = storedRecord ? deHashObject(storedRecord) : null
      setTableGroups(savedConfig ?? [])
    } else {
      store(`${localStorageKey}.${currentHash}`, hashObjects([]))
      setTableGroups([])
    }
  }, [availableGroupings, localStorageKey, tryLoad, deHashObject, hashObjects, store])

  /** Load the available dates from localStorage */
  useEffect(() => {
    if (!localStorageKey) return
    const currentHash = hashObjects(availableDates)
    const storedRecord = tryLoad(`${localStorageKey}.${currentHash}`)
    if (storedRecord) {
      const savedConfig = storedRecord ? deHashObject(storedRecord) : null
      setTableDates(savedConfig ?? { startDate: null, endDate: null })
    } else {
      store(`${localStorageKey}.${currentHash}`, hashObjects([]))
      setTableDates({ startDate: null, endDate: null })
    }
  }, [availableDates, localStorageKey, tryLoad, deHashObject, hashObjects, store])

  /** Load the additional date configuration from localStorage */
  useEffect(() => {
    if (!localStorageKey || !showTableConfiguration.includes('configureDates')) return
    const currentHash = hashObjects(editableColumns)
    const storedRecord = tryLoad(`${localStorageKey}.${currentHash}`)
    if (storedRecord) {
      const savedConfig = storedRecord ? deHashObject(storedRecord) : null
      setTableAdditionalDates(savedConfig ?? { editableColumns: editableColumns.map((column) => ({ dateRange: 'qd', column: column })), dateRange1: quickDate, dateRange2: quickDate })
    } else {
      store(`${localStorageKey}.${currentHash}`, hashObjects({ editableColumns: editableColumns.map((column) => ({ dateRange: 'qd', column: column })), dateRange1: quickDate, dateRange2: quickDate }))
      setTableAdditionalDates({ editableColumns: editableColumns.map((column) => ({ dateRange: 'qd', column: column })), dateRange1: quickDate, dateRange2: quickDate })
    }
  }, [editableColumns, localStorageKey, tryLoad, deHashObject, hashObjects, store, quickDate, showTableConfiguration])

  // Local Storage Save Functions
  const saveGroupConfiguration = (newGroupConfig) => {
    const hashedNewConfig = hashObjects(newGroupConfig)
    const hashedAEConfig = hashObjects(availableGroupings)
    store(`${localStorageKey}.${hashedAEConfig}`, hashedNewConfig)
    setTableGroups(newGroupConfig)
  }
  const saveDateConfiguration = (newDateConfig) => {
    const hashedNewConfig = hashObjects(newDateConfig)
    const hashedAEConfig = hashObjects(availableDates)
    store(`${localStorageKey}.${hashedAEConfig}`, hashedNewConfig)
    setTableDates(newDateConfig)
  }
  const saveColumnConfiguration = (newColumnConfig) => {
    const hashedNewConfig = hashObjects(newColumnConfig)
    const hashedAEConfig = hashObjects(columnConfig)
    store(`${localStorageKey}.${hashedAEConfig}`, hashedNewConfig)
    setTableConfig(newColumnConfig)
  }
  const saveAdditionalDateConfiguration = (newAdditionalDateConfig) => {
    const hashedNewConfig = hashObjects(newAdditionalDateConfig)
    const hashedAEConfig = hashObjects(editableColumns)
    store(`${localStorageKey}.${hashedAEConfig}`, hashedNewConfig)
    setTableAdditionalDates(newAdditionalDateConfig)
  }

  const initialExpansionState = useMemo(() => {
    if (initialExpandDepth !== undefined && !!tableData?.length) {
      return getInitialExpandedState(tableData, initialExpandDepth, (row) => row.data)
    }
    return true
  }, [tableData, initialExpandDepth])

  const replaceDateRanges = () => {
    return tableConfig.map((column) => {
      const additionalDate = tableAdditionalDates?.editableColumns ? tableAdditionalDates.editableColumns.find((c) => c.column === column.id) : null
      if (additionalDate?.dateRange === 'qd' || !additionalDate) {
        return {
          ...column,
          accessorKey: mapAccessorKey(column)?.replace('$DR', 'qd')
        }
      }
      return {
        ...column,
        accessorKey: mapAccessorKey(column)?.replace('$DR', additionalDate?.value),
        header: (
          <div className='flex flex-row items-center gap-2'>{column.header}
            <Tooltip
              title={<CustomTooltip text={additionalDateTooltipText} style={additionalDateTooltipStyle} />}
            >
              <div className='flex flex-row items-center gap-2'>
                <Icon name='info' />
              </div>
            </Tooltip>
          </div>
        )
      }
    })
  }
  const { expandState, setExpandState } = usePresentationTableExpandState(initialExpansionState)

  /** Derive the column configuration, sorting, and footer */
  const { columns, defaultSort, includeFooter } = useColumns(replaceDateRanges(), data?.total || [])
  /** Compatibility with <HideIfNoData /> */
  useHideIfNoDataWhen(() => !isLoading && !tableData?.length, [tableData, isLoading])

  return (
    <div>
      <TableConfigModal
        isOpen={columnConfigOpen}
        onClose={() => setColumnConfigOpen(false)}
        columns={tableConfig}
        originalColumns={columnConfig}
        onColumnSave={saveColumnConfiguration}
        onGroupSave={saveGroupConfiguration}
        onDateSave={saveDateConfiguration}
        availableGroupings={availableGroupings}
        activeGroups={tableGroups}
        defaultGroups={variables?.grouping ?? []}
        availableDates={(availableDates && availableDates.startDate && availableDates.endDate) ? availableDates : { startDate: dayjs().subtract(1, 'year').format(DATE_FORMAT), endDate: dayjs().format(DATE_FORMAT) }}
        additionalDateTooltipStyle={additionalDateTooltipStyle}
        additionalDates={tableAdditionalDates}
        onAdditionalDateSave={saveAdditionalDateConfiguration}
        editableColumns={tableAdditionalDates}
        showTableConfiguration={showTableConfiguration}
        availableDateRanges={availableDateRanges}
      />
      <div className='flex flex-row justify-end gap-2'>
        {configurationOptions && localStorageKey && showTableConfiguration.length > 0 && (
          <div className='flex justify-end mb-2'>
            <SydButton onClick={() => setColumnConfigOpen(!columnConfigOpen)}><Icon name='settings' /></SydButton>
          </div>
        )}
        {allowExport && (
          <div className='flex justify-end mb-2'>
            <ExportTableToggle pipeline={pipeline} tableData={tableData} columns={columns} isLoading={isLoading} />
          </div>
        )}
      </div>
      <OpalPresentationTable
        className={className}
        componentClasses={componentClasses}
        componentStyles={{
          ...componentStyles,
          highlight: componentStyles?.highlight || { backgroundColor: 'var(--color-highlight-500)' }
        }}
        style={style}
        data={tableData || []}
        columns={columns.filter((column) => {
          return column.defaultShown ?? true
        })}
        defaultSort={defaultSort}
        includeHeader={headerMode !== 'hidden'}
        includeFooter={includeFooter}
        expandState={expandState}
        onExpandStateChange={setExpandState}
        isLoading={isLoading}
        emptyStateText={emptyStateText}
        {...tableProps}
      />
    </div>
  )
}

PipelinePresentationTable.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

  /** The table column configuration / mapping */
  columnConfig: PropTypes.array,

  /** Header mode */
  headerMode: PropTypes.oneOf(['hidden', 'default']),

  className: PropTypes.string,
  style: PropTypes.object,

  dataAccessor: PropTypes.string,

  /** List of variables */
  variables: PropTypes.object,

  /** Specifies classes to be applied to subcomponents of the table */
  componentClasses: PropTypes.shape({
    header: PropTypes.string,
    cell: PropTypes.string,
    footer: PropTypes.string
  }),
  componentStyles: PropTypes.shape({
    header: PropTypes.object,
    cell: PropTypes.object,
    highlight: PropTypes.object,
    footer: PropTypes.object
  }),
  /** Empty state text, set to an empty string to hide */
  emptyStateText: PropTypes.string,
  /** Initial depth to expand the table to */
  initialExpandDepth: PropTypes.number,
  /** Whether to show the export button */
  allowExport: PropTypes.bool,
  configurationOptions: PropTypes.shape({
    showTableConfiguration: PropTypes.arrayOf(PropTypes.string),
    localStorageKey: PropTypes.string,
    availableGroupings: PropTypes.arrayOf(PropTypes.string),
    availableDates: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string
    }),
    additionalDateTooltipText: PropTypes.string,
    additionalDateTooltipStyle: PropTypes.object,
    editableColumns: PropTypes.arrayOf(PropTypes.string),
    availableDateRanges: PropTypes.arrayOf(PropTypes.string)
  })
}

PipelinePresentationTable.defaultProps = {
  headerMode: 'default',
  dataAccessor: 'data',
  emptyStateText: 'No Data Available',
  allowExport: false,
  configurationOptions: {
    localStorageKey: '',
    showTableConfiguration: ['configureColumns', 'configureGroupings', 'configureDates'],
    availableGroupings: [],
    availableDates: {
      startDate: null,
      endDate: null
    },
    additionalDateTooltipStyle: {
      color: 'var(--color-neutral-0)',
      padding: '10px',
      borderRadius: '5px',
      fontSize: '12px',
      fontWeight: 'bold'
    },
    editableColumns: [],
    additionalDateTooltipText: 'This Column is using an alternate date range',
    availableDateRanges: []
  }
}

export default PipelinePresentationTable
